import { BranchStateService, CustomCodeEventDetail, ProjectService } from '@alpha-core/platform-ui';
import {
    ADMIN_INBOX_RESPONSE,
    ENQUIRY_INBOX_RESPONSE,
    INBOX_LIST_TYPE,
    INBOX_RESPONSE,
    IinboxFilter,
    InboxAttributes,
    InboxName,
    InboxRelation,
} from '@alpha-core/platform-ui/interfaces/admin-filter.interface';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, lastValueFrom, of } from 'rxjs';
import { environment } from '../../environments/environment';

declare var ap: any;

@Injectable({ providedIn: 'root' })
export class InboxService {
    private inboxRefreshSubject = new BehaviorSubject<Boolean>(false);
    onInboxRefresh$ = this.inboxRefreshSubject.asObservable();
    private messageService = inject(MessageService);
    private httpClient = inject(HttpClient);
    private projectService = inject(ProjectService);
    private allInboxConfigs: Record<INBOX_LIST_TYPE, IinboxFilter> | null = null;

    /**
     * Build HttpParams with branch parameter
     * Uses BranchStateService to get branch from URL (defaults to 'main' if not present)
     */
    private buildParams(): HttpParams {
        const branch = BranchStateService.getBranchFromUrl();
        return new HttpParams().set('branch', branch);
    }

    getAdminInbox() {
        const currentProject = this.projectService.fetchCurrentProject();
        const getCurrentTag = currentProject?.currentTag ? true : false;
        return this.httpClient
            .post<ADMIN_INBOX_RESPONSE>(
                `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-config-server/inbox/admin/fetch`,

                {
                    projectId: currentProject?.projectId,
                    getCurrentTag,
                },
                { params: this.buildParams() },
            )
            .pipe(
                catchError((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: error?.statusText || 'Failed',
                        detail: error?.error?.message || 'Unable to fetch admin inbox configuration',
                    });
                    return of(null);
                }),
            );
    }

    getInbox() {
        const currentProject = this.projectService.fetchCurrentProject();
        const getCurrentTag = currentProject?.currentTag ? true : false;
        return this.httpClient
            .post<INBOX_RESPONSE>(
                `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-config-server/inbox/fetch`,
                {
                    projectId: currentProject?.projectId,
                    getCurrentTag,
                },
                { params: this.buildParams() },
            )
            .pipe(
                catchError((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: error?.statusText || 'Failed',
                        detail: error?.error?.message || 'Unable to fetch inbox configuration',
                    });
                    return of(null);
                }),
            );
    }

    getEnquiryInbox() {
        const currentProject = this.projectService.fetchCurrentProject();
        const getCurrentTag = currentProject?.currentTag ? true : false;
        return this.httpClient
            .post<ENQUIRY_INBOX_RESPONSE>(
                `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-config-server/inbox/enquiry/fetch`,
                {
                    projectId: currentProject?.projectId,
                    getCurrentTag,
                },
                { params: this.buildParams() },
            )
            .pipe(
                catchError((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: error?.statusText || 'Failed',
                        detail: error?.error?.message || 'Unable to fetch Enquiry inbox configuration',
                    });
                    return of(null);
                }),
            );
    }

    async getAllInboxFilterConfig(filters: {
        inboxName?: InboxName;
        inboxType?: InboxRelation;
        attributes: InboxAttributes[];
    }) {
        // if (!this.allInboxConfigs) {
        const currentProject = this.projectService.fetchCurrentProject();
        const getCurrentTag = currentProject?.currentTag ? true : false;
        this.allInboxConfigs = await lastValueFrom(
            this.httpClient
                .post<Record<INBOX_LIST_TYPE, IinboxFilter>>(
                    `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-config-server/inbox/attributes/fetch`,
                    {
                        projectId: currentProject?.projectId,
                        getCurrentTag,
                        ...filters,
                    },
                    { params: this.buildParams() },
                )
                .pipe(
                    catchError((error: any) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: error?.statusText || 'Failed',
                            detail: error?.error?.message || 'Unable to fetch Enquiry inbox configuration',
                        });
                        return of(null);
                    }),
                ),
        );
        // }
        return this.allInboxConfigs;
    }

    fetchReattemptStatus() {
        return this.httpClient.post(`${environment.ALPHA_UI_ADMIN_SERVICE_URL}/re-attempt-status/status`, {
            status: ['failed', 'ready', 'processing'],
        });
    }

    onInboxRefresh() {
        this.inboxRefreshSubject.next(true);
    }

    cleanMappingPathString(path: string) {
        if (path.startsWith('$.')) {
            path = path.slice(2); // Removes `$.`
        }

        // Split the string by `.`
        let segments = path.split('.');

        // Remove double quotes from each segment
        segments = segments.map((segment) => {
            // Remove leading double quote if present
            if (segment.startsWith('"')) {
                segment = segment.slice(1);
            }
            // Remove trailing double quote if present
            if (segment.endsWith('"')) {
                segment = segment.slice(0, -1);
            }
            return segment;
        });

        // Join the segments back together
        path = segments.join('.');

        console.log(path);
        return path;
    }

    async executeAction(actions: Action[] = [], task: CustomCodeEventDetail) {
        const triggerResult: any = [];
        const params = { eventDetail: task };
        for (const action of actions) {
            try {
                const emmitLoaderEvent = true;
                const responsePromise = ap.trigger(action, params, triggerResult, emmitLoaderEvent);
                if (!action.isAsync) {
                    const response = await responsePromise;
                    triggerResult.push({ action, response: response?.data });
                    await this.handleActionResponse(action, response, triggerResult);
                } else {
                    triggerResult.push({ action, response: responsePromise });
                    responsePromise.then(async (res: any) => {
                        await this.handleActionResponse(action, res, triggerResult);
                    });
                }
            } catch (e) {
                console.error(e);
                if (action.stopExecution && !action.isAsync) {
                    return;
                }
            }
        }
    }
    private async handleActionResponse(action: Action, response: any, triggerResult: any) {
        if (action) {
            ap.toast.toastMessage(action, response?.status, triggerResult);
        }
    }
}

export interface Action {
    id?: string;
    name?: string;
    type: string;
    actionName?: string;
    config: any;
    stopExecution?: boolean;
    isAsync?: boolean;
    toastConfig?: IToastConfig | null;
    [key: string]: any;
}

interface IToastConfig {
    addToast: boolean;
    toggled: boolean;
    toasts: any[];
}
